import { OutboundCallBadge } from '../../interviews/components/OutboundCallDetails';
import { formatInterviewQuality } from '@/lib/interview-quality';
import { useEffect, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Loader2,
  MoreHorizontal,
  Pause,
  Play,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { CopyIdButton, MobileSortControl } from '@/components/shared';
import { useMediaQuery } from '@/lib/use-media-query';
import { fetchBackofficeInterviewRecording } from '../api';
import { formatDateTime, formatShortId, formatStatus } from '../format';
import type { BackofficeProjectInterviewRow, InterviewSortBy, SortDirection } from '../types';

const SORTABLE_HEADERS: Record<InterviewSortBy, string> = {
  study: 'Study',
  duration: 'Duration',
  createdAt: 'Created date',
};

const BASE_MOBILE_SORT_OPTIONS = [
  { value: 'duration', label: SORTABLE_HEADERS.duration },
  { value: 'createdAt', label: SORTABLE_HEADERS.createdAt },
] satisfies Array<{ value: InterviewSortBy; label: string }>;

export interface ProjectInterviewsTableProps {
  interviews: BackofficeProjectInterviewRow[];
  projectTitle: string;
  showStudyMobileContext?: boolean;
  showStudySort?: boolean;
  showStudyOwner?: boolean;
  sortBy: InterviewSortBy;
  sortDirection: SortDirection;
  onSortChange: (sortBy: InterviewSortBy) => void;
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) {
    return 'Not available';
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

function formatSource(source: string): string {
  if (source === 'direct') return 'Link';
  return formatStatus(source);
}

function formatStudyOwner(interview: BackofficeProjectInterviewRow): string {
  return interview.study.ownerName ?? interview.study.ownerEmail ?? 'Unassigned';
}

function isEffectivelyIncluded(interview: BackofficeProjectInterviewRow): boolean {
  if (interview.includedInAnalysis !== null) {
    return interview.includedInAnalysis;
  }
  return interview.qualityStatus === 'completed';
}

function InclusionBadge({ interview }: { interview: BackofficeProjectInterviewRow }) {
  const included = isEffectivelyIncluded(interview);
  const isDefault = interview.includedInAnalysis === null;

  return (
    <span
      className={[
        'inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-small font-medium',
        included ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-100 text-neutral-700',
      ].join(' ')}
    >
      {included ? 'Yes' : 'No'}
      {isDefault ? ' (default)' : ''}
    </span>
  );
}

function SortButton({
  label,
  field,
  sortBy,
  sortDirection,
  onSortChange,
}: {
  label: string;
  field: InterviewSortBy;
  sortBy: InterviewSortBy;
  sortDirection: SortDirection;
  onSortChange: (sortBy: InterviewSortBy) => void;
}) {
  const active = field === sortBy;
  const Icon = active ? (sortDirection === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;

  return (
    <button
      type="button"
      className="focus-ring inline-flex items-center gap-1 rounded-md text-left font-semibold text-neutral-600"
      aria-label={`Sort by ${label}`}
      onClick={() => onSortChange(field)}
    >
      <span>{label}</span>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  );
}

export function ProjectInterviewsTable({
  interviews,
  projectTitle,
  showStudyMobileContext = false,
  showStudySort = false,
  showStudyOwner = false,
  sortBy,
  sortDirection,
  onSortChange,
}: ProjectInterviewsTableProps) {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [activeInterviewId, setActiveInterviewId] = useState<string | null>(null);
  const [activeRecordingUrl, setActiveRecordingUrl] = useState<string | null>(null);
  const [playingInterviewId, setPlayingInterviewId] = useState<string | null>(null);
  const [loadingInterviewId, setLoadingInterviewId] = useState<string | null>(null);
  const [unavailableInterviewIds, setUnavailableInterviewIds] = useState<Set<string>>(
    () => new Set()
  );

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !activeInterviewId || !activeRecordingUrl) {
      return;
    }

    audio.src = activeRecordingUrl;
    void audio.play().catch(() => {
      setPlayingInterviewId(null);
      setUnavailableInterviewIds((current) => new Set(current).add(activeInterviewId));
    });
  }, [activeInterviewId, activeRecordingUrl]);

  function navigateToInterview(interviewId: string) {
    void navigate(`/interviews/${interviewId}`);
  }

  async function toggleInterviewPlayback(interview: BackofficeProjectInterviewRow) {
    const audio = audioRef.current;
    if (activeInterviewId === interview.id && playingInterviewId === interview.id) {
      audio?.pause();
      return;
    }
    if (activeInterviewId === interview.id && activeRecordingUrl && audio) {
      void audio.play().catch(() => {
        setPlayingInterviewId(null);
        setUnavailableInterviewIds((current) => new Set(current).add(interview.id));
      });
      return;
    }

    setLoadingInterviewId(interview.id);
    setUnavailableInterviewIds((current) => {
      const next = new Set(current);
      next.delete(interview.id);
      return next;
    });

    try {
      const response = await fetchBackofficeInterviewRecording(interview.id);
      if (!response.recording) {
        setUnavailableInterviewIds((current) => new Set(current).add(interview.id));
        return;
      }
      setActiveInterviewId(interview.id);
      setActiveRecordingUrl(response.recording.url);
    } catch {
      setUnavailableInterviewIds((current) => new Set(current).add(interview.id));
    } finally {
      setLoadingInterviewId(null);
    }
  }

  const audioElement = (
    <audio
      ref={audioRef}
      className="hidden"
      onPlay={() => setPlayingInterviewId(activeInterviewId)}
      onPause={() => setPlayingInterviewId(null)}
      onEnded={() => setPlayingInterviewId(null)}
      onError={() => {
        if (activeInterviewId) {
          setUnavailableInterviewIds((current) => new Set(current).add(activeInterviewId));
        }
        setPlayingInterviewId(null);
      }}
    />
  );

  if (isMobile) {
    const mobileSortOptions = showStudySort
      ? [{ value: 'study', label: SORTABLE_HEADERS.study } as const, ...BASE_MOBILE_SORT_OPTIONS]
      : BASE_MOBILE_SORT_OPTIONS;

    return (
      <div>
        {audioElement}
        <MobileSortControl
          label="Sort interviews"
          options={mobileSortOptions}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={onSortChange}
        />
        <div className="divide-y divide-neutral-100">
          {interviews.map((interview) => (
            <article
              key={interview.id}
              role="button"
              tabIndex={0}
              aria-label={`View interview ${formatShortId(interview.id)}`}
              className="cursor-pointer px-4 py-4 hover:bg-backoffice-50 focus:bg-backoffice-50 focus:outline-none"
              onClick={() => navigateToInterview(interview.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  navigateToInterview(interview.id);
                }
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium tabular-nums text-neutral-900">
                    {formatDateTime(interview.createdAt)}
                  </div>
                  {showStudyMobileContext ? (
                    <>
                      <div className="mt-1 truncate text-small text-neutral-600">
                        {interview.study.title}
                      </div>
                      {showStudyOwner ? (
                        <div className="mt-0.5 truncate text-small text-neutral-500">
                          {formatStudyOwner(interview)}
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </div>
                <button
                  type="button"
                  disabled={loadingInterviewId === interview.id}
                  className="focus-ring inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-neutral-500 hover:bg-neutral-100 disabled:cursor-not-allowed disabled:text-neutral-300"
                  aria-label={`${
                    playingInterviewId === interview.id ? 'Pause' : 'Play'
                  } interview ${formatShortId(interview.id)}`}
                  title={
                    unavailableInterviewIds.has(interview.id)
                      ? 'Playback unavailable'
                      : playingInterviewId === interview.id
                        ? 'Pause recording'
                        : 'Play recording'
                  }
                  onClick={(event) => {
                    event.stopPropagation();
                    void toggleInterviewPlayback(interview);
                  }}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  {loadingInterviewId === interview.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : playingInterviewId === interview.id ? (
                    <Pause className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Play className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {interview.outboundCall ? (
                  <OutboundCallBadge call={interview.outboundCall} />
                ) : null}
                <span className="inline-flex rounded-full bg-neutral-100 px-2.5 py-1 text-small font-medium text-neutral-700">
                  {formatInterviewQuality(interview)}
                </span>
                <span className="text-small tabular-nums text-neutral-600">
                  {formatDuration(interview.durationSeconds)}
                </span>
              </div>
            </article>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      {audioElement}
      <table
        className={[
          showStudyOwner ? 'min-w-[1480px]' : 'min-w-[1280px]',
          'w-full border-collapse text-left',
        ].join(' ')}
      >
        <thead className="border-b border-neutral-200 bg-white text-small text-neutral-500">
          <tr>
            <th scope="col" className="w-12 px-4 py-3">
              <span className="sr-only">Select</span>
            </th>
            <th scope="col" className="min-w-[130px] px-4 py-3 font-semibold text-neutral-600">
              ID
            </th>
            <th scope="col" className="min-w-[180px] px-4 py-3 font-semibold text-neutral-600">
              {showStudySort ? (
                <SortButton
                  label={SORTABLE_HEADERS.study}
                  field="study"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSortChange={onSortChange}
                />
              ) : (
                'Study'
              )}
            </th>
            {showStudyOwner ? (
              <th scope="col" className="min-w-[200px] px-4 py-3 font-semibold text-neutral-600">
                Owner
              </th>
            ) : null}
            <th scope="col" className="min-w-[130px] px-4 py-3 font-semibold text-neutral-600">
              <SortButton
                label={SORTABLE_HEADERS.duration}
                field="duration"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            </th>
            <th scope="col" className="min-w-[160px] px-4 py-3 font-semibold text-neutral-600">
              Quality status
            </th>
            <th scope="col" className="min-w-[160px] px-4 py-3 font-semibold text-neutral-600">
              Call status
            </th>
            <th scope="col" className="min-w-[130px] px-4 py-3 font-semibold text-neutral-600">
              Source
            </th>
            <th scope="col" className="min-w-[180px] px-4 py-3 font-semibold text-neutral-600">
              Included in analysis
            </th>
            <th scope="col" className="min-w-[190px] px-4 py-3 font-semibold text-neutral-600">
              <SortButton
                label={SORTABLE_HEADERS.createdAt}
                field="createdAt"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            </th>
            <th scope="col" className="w-16 px-4 py-3">
              <span className="sr-only">Play recording</span>
            </th>
            <th scope="col" className="w-14 px-4 py-3">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100 text-body">
          {interviews.map((interview) => (
            <tr
              key={interview.id}
              className="cursor-pointer hover:bg-backoffice-50"
              tabIndex={0}
              onClick={() => navigateToInterview(interview.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  navigateToInterview(interview.id);
                }
              }}
              aria-label={`View interview ${formatShortId(interview.id)}`}
            >
              <td className="px-4 py-4">
                <input
                  type="checkbox"
                  aria-label={`Select interview ${formatShortId(interview.id)}`}
                  className="h-4 w-4 rounded border-neutral-300 text-lavender-600"
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                />
              </td>
              <td className="px-4 py-4 text-neutral-700">
                <div className="group inline-flex items-center gap-2">
                  <span className="font-medium tabular-nums text-neutral-700">
                    {formatShortId(interview.id)}
                  </span>
                  <CopyIdButton value={interview.id} label="Copy interview ID" />
                </div>
              </td>
              <td className="px-4 py-4 text-neutral-700">
                <Link
                  to={`/projects/${interview.study.id}`}
                  className="focus-ring rounded-sm font-medium text-neutral-700 hover:text-lavender-700"
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  {interview.study.title}
                </Link>
              </td>
              {showStudyOwner ? (
                <td className="px-4 py-4 text-neutral-700">
                  {interview.study.ownerUserId ? (
                    <Link
                      to={`/users/${interview.study.ownerUserId}`}
                      className="focus-ring rounded-sm font-medium text-neutral-900 hover:text-backoffice-600"
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      {formatStudyOwner(interview)}
                    </Link>
                  ) : (
                    <span>{formatStudyOwner(interview)}</span>
                  )}
                </td>
              ) : null}
              <td className="px-4 py-4 tabular-nums text-neutral-700">
                {formatDuration(interview.durationSeconds)}
              </td>
              <td className="px-4 py-4">
                <span className="inline-flex rounded-full bg-neutral-100 px-2.5 py-1 text-small font-medium text-neutral-700">
                  {formatInterviewQuality(interview)}
                </span>
              </td>
              <td className="px-4 py-4">
                {interview.outboundCall ? <OutboundCallBadge call={interview.outboundCall} /> : '—'}
              </td>
              <td className="px-4 py-4 text-neutral-700 capitalize">
                {formatSource(interview.source)}
              </td>
              <td className="px-4 py-4">
                <InclusionBadge interview={interview} />
              </td>
              <td className="px-4 py-4 text-neutral-700">{formatDateTime(interview.createdAt)}</td>
              <td className="px-4 py-4">
                <button
                  type="button"
                  disabled={loadingInterviewId === interview.id}
                  className="focus-ring inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 hover:bg-neutral-100 disabled:cursor-not-allowed disabled:text-neutral-300"
                  aria-label={`${
                    playingInterviewId === interview.id ? 'Pause' : 'Play'
                  } interview ${formatShortId(interview.id)}`}
                  title={
                    unavailableInterviewIds.has(interview.id)
                      ? 'Playback unavailable'
                      : playingInterviewId === interview.id
                        ? 'Pause recording'
                        : 'Play recording'
                  }
                  onClick={(event) => {
                    event.stopPropagation();
                    void toggleInterviewPlayback(interview);
                  }}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  {loadingInterviewId === interview.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : playingInterviewId === interview.id ? (
                    <Pause className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Play className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              </td>
              <td className="px-4 py-4">
                <button
                  type="button"
                  className="focus-ring inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 hover:bg-backoffice-50"
                  aria-label={`Actions for interview ${formatShortId(interview.id)} in ${projectTitle}`}
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
